import { useState } from "react";
import { Send, Loader2, ChevronDown, CheckCircle2, AlertTriangle, Users, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export const TricountPush = ({ tripId, prefillUrl, members = [], prefillMap = {}, onRefresh, autoSync = false }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(prefillUrl || "");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tc, setTc] = useState(null);
  const [nameMap, setNameMap] = useState({});
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [autoOn, setAutoOn] = useState(autoSync);
  const [autoBusy, setAutoBusy] = useState(false);

  const toggleAutoSync = async () => {
    const next = !autoOn;
    setAutoBusy(true);
    try {
      await api.post(`/trips/${tripId}/tricount/auto-sync`, { enabled: next });
      setAutoOn(next);
      toast.success(next
        ? "Auto-sync aan — nieuwe verdeelde uitgaven gaan voortaan meteen naar je Tricount"
        : "Auto-sync uit — je verstuurt voortaan zelf met de knop");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAutoBusy(false);
    }
  };

  const importFromTricount = async () => {
    if (!url.trim()) return;
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post(`/trips/${tripId}/tricount/import`, { share_url: url.trim() }, { timeout: 120000 });
      setImportResult(data);
      if (data.imported > 0) {
        toast.success(`${data.imported} uitgaven geïmporteerd uit "${data.tricount_title}"`);
        onRefresh?.();
      } else {
        toast.info("Niets nieuws om te importeren — alles staat al in VoltRoute");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const checkNames = async () => {
    if (!url.trim()) return;
    setChecking(true);
    setResult(null);
    try {
      const { data } = await api.post(`/trips/${tripId}/tricount/members`, { share_url: url.trim() }, { timeout: 60000 });
      setTc(data);
      const init = {};
      members.forEach((m) => {
        const stored = (data.name_map || {})[m] || prefillMap?.[m];
        if (stored && data.members.includes(stored)) init[m] = stored;
        else init[m] = data.members.find((x) => x.trim().toLowerCase() === m.trim().toLowerCase()) || "";
      });
      setNameMap(init);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const push = async () => {
    if (!url.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const cleanMap = Object.fromEntries(Object.entries(nameMap).filter(([, v]) => v));
      const { data } = await api.post(`/trips/${tripId}/tricount/push`,
        { share_url: url.trim(), name_map: Object.keys(cleanMap).length ? cleanMap : null }, { timeout: 120000 });
      setResult(data);
      if (data.pushed > 0) toast.success(`${data.pushed} uitgaven naar Tricount "${data.tricount_title}" gestuurd`);
      else toast.info("Alles stond al in je Tricount — niets nieuws om te versturen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <button type="button" data-testid="tricount-toggle" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-semibold text-[#3A6EA5] hover:text-[#2a5580] transition-[color] duration-200">
        <Send className="w-3.5 h-3.5" /> Stuur verdeelde uitgaven naar Tricount
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 bg-secondary/40 rounded-xl p-3" data-testid="tricount-panel">
          <p className="text-[11px] text-muted-foreground mb-2">
            Plak de deellink van je Tricount (bv. https://tricount.com/tABC123xyz). Klik daarna op <span className="font-semibold">"Namen koppelen"</span> om je reisgenoten aan de juiste Tricount-leden te koppelen — de koppeling wordt onthouden. Al verstuurde uitgaven worden niet dubbel gepusht. <span className="italic">Onofficiële koppeling: kan breken als Tricount iets wijzigt.</span>
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <input data-testid="tricount-url-input" value={url} onChange={(e) => { setUrl(e.target.value); setTc(null); }}
              placeholder="https://tricount.com/t..." disabled={busy || checking}
              className="flex-1 bg-card rounded-xl px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
            {members.length > 0 && (
              <button data-testid="tricount-check-names" onClick={checkNames} disabled={checking || busy || !url.trim()}
                className="rounded-full border border-[#3A6EA5]/40 text-[#3A6EA5] hover:bg-[#3A6EA5]/10 disabled:opacity-40 text-xs font-bold py-2 px-4 flex items-center justify-center gap-1.5 transition-[background-color] duration-200 shrink-0">
                {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                {checking ? "Ophalen..." : "Namen koppelen"}
              </button>
            )}
            <button data-testid="tricount-push-button" onClick={push} disabled={busy || checking || importing || !url.trim()}
              className="rounded-full bg-primary hover:bg-[#1B4332] disabled:opacity-40 text-primary-foreground text-xs font-bold py-2 px-4 flex items-center justify-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200 shrink-0">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {busy ? "Versturen..." : "Verstuur naar Tricount"}
            </button>
            <button data-testid="tricount-import-button" onClick={importFromTricount} disabled={busy || checking || importing || !url.trim()}
              title="Haal alle uitgaven uit je Tricount op en zet ze in je budget + kostenverdeling (dubbels worden overgeslagen)"
              className="rounded-full border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-40 text-xs font-bold py-2 px-4 flex items-center justify-center gap-1.5 transition-[background-color] duration-200 shrink-0">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {importing ? "Importeren..." : "Importeer uit Tricount"}
            </button>
          </div>
          {importResult && (
            <p className="mt-2 text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5 font-semibold text-[#2D6A4F]" data-testid="tricount-import-result">
              <CheckCircle2 className="w-3.5 h-3.5" /> {importResult.imported} geïmporteerd uit "{importResult.tricount_title}"
              {(importResult.skipped_done > 0 || importResult.skipped_own > 0) && (
                <span className="text-muted-foreground font-normal">· {importResult.skipped_done + importResult.skipped_own} stonden er al in</span>
              )}
              {(importResult.new_members || []).length > 0 && (
                <span className="text-muted-foreground font-normal">· nieuwe reisgenoten: {importResult.new_members.join(", ")}</span>
              )}
            </p>
          )}
          <label className="flex items-start gap-2 mt-2.5 text-xs cursor-pointer select-none" data-testid="tricount-autosync-label">
            <input type="checkbox" data-testid="tricount-autosync-toggle" checked={autoOn} disabled={autoBusy}
              onChange={toggleAutoSync} className="mt-0.5 accent-[#2D6A4F] w-3.5 h-3.5" />
            <span>
              <span className="font-semibold">Automatisch synchroniseren</span>
              <span className="text-muted-foreground"> — elke nieuwe verdeelde uitgave vloeit meteen naar je gekoppelde Tricount (met je naamkoppeling)</span>
            </span>
          </label>
          {tc && (
            <div className="mt-2.5 bg-card rounded-xl p-3" data-testid="tricount-name-mapping">
              <p className="text-[11px] font-semibold mb-1.5">Leden in "{tc.title}": <span className="font-normal text-muted-foreground">{tc.members.join(", ")}</span></p>
              <div className="space-y-1.5">
                {members.map((m) => (
                  <div key={m} className="flex items-center gap-2 text-xs" data-testid={`tricount-map-row-${m}`}>
                    <span className="w-28 sm:w-36 truncate font-semibold">{m}</span>
                    <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                    <select data-testid={`tricount-map-select-${m}`} value={nameMap[m] || ""}
                      onChange={(e) => setNameMap((nm) => ({ ...nm, [m]: e.target.value }))}
                      className={`flex-1 bg-secondary rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-primary ${nameMap[m] ? "" : "text-red-600"}`}>
                      <option value="">— kies lid in Tricount —</option>
                      {tc.members.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {members.some((m) => !nameMap[m]) && (
                <p className="text-[11px] text-amber-700 mt-1.5">Niet-gekoppelde namen: uitgaven van/voor die persoon worden overgeslagen. Voeg het lid eventueel eerst toe in Tricount en klik opnieuw op "Namen koppelen".</p>
              )}
            </div>
          )}
          {result && (
            <div className="mt-2.5 text-xs space-y-1" data-testid="tricount-result">
              <p className="flex items-center gap-1.5 font-semibold text-[#2D6A4F]">
                <CheckCircle2 className="w-3.5 h-3.5" /> {result.pushed} verstuurd naar "{result.tricount_title}"
                {result.already_synced > 0 && <span className="text-muted-foreground font-normal">· {result.already_synced} stonden er al in</span>}
              </p>
              {(result.skipped || []).length > 0 && (
                <div className="text-amber-700" data-testid="tricount-skipped">
                  <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> {result.skipped.length} overgeslagen:</p>
                  {result.skipped.map((s, i) => <p key={i} className="pl-5">"{s.description}" — {s.reason}</p>)}
                  <p className="pl-5 text-muted-foreground">Koppel de namen via "Namen koppelen" en verstuur opnieuw.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
